import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, session } from '../api/client.js';
import { disconnectWalletConnect } from '../web3/siwe.js';
import { useSiweLogin } from '../web3/useSiweLogin.js';
import { GoldWordmark } from './GoldWordmark.js';
import { cn } from '../lib/classnames.js';
import { CreditBadge } from './CreditBadge.js';
import { ThemeToggle } from './Theme.js';
import { ENTERED_KEY } from '../pages/Entry.js';

/**
 * Top-level app frame.
 *
 * Visual intent:
 *   - The top header reads as a book frontispiece: hairline base rule, a small
 *     fleuron mark between the logo and the wordmark, no chunky backdrop or
 *     left-rail accent. Same chrome works in light + dark via semantic tokens.
 *   - Primary nav (md+) is hairline serif links; the active link gets a tiny
 *     gilded underline (no pill, no background swap).
 *   - CreditBadge + ThemeToggle live in a single small "instruments" cluster
 *     at the right edge.
 *   - The mobile bottom-nav drops the uppercase-tracked-tiny label (the
 *     eyebrow-as-everything tell) in favour of sentence-case display labels
 *     and a gilded dot over the active icon.
 *   - Footer is a quiet colophon line, not a generic copyright row.
 *
 * Every existing prop, hook, query, and route binding is preserved — only the
 * presentation changes.
 */
export function Layout() {
  const location = useLocation();
  const onLanding = location.pathname === '/';
  const onNew = location.pathname === '/new';
  const onBuy = location.pathname === '/buy';
  // Full-bleed = flush-to-edges immersive scene: no frame padding and no
  // trailing footer, so the scene ends flush instead of scrolling on into a
  // black band. Landing, /buy, and the /new House of Gilding all qualify.
  const fullBleed = onLanding || onBuy || onNew;
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    staleTime: 30_000,
  });
  const authed = meQuery.data?.authenticated === true;
  // Landing hides the bottom nav (it would cover the foot of the pinned
  // scene). /new and /buy are exceptions: signed-in app pages whose scenes are
  // full-bleed but which KEEP the bottom nav so the visitor can navigate out.
  const showBottomNav = authed && (onNew || onBuy || !fullBleed);
  const scrolled = useScrolled();

  // Reset scroll to the top on every route change. Without this a navigation
  // from deep in the tall landing scroll (e.g. the foot-of-hero "Gild free"
  // CTA) lands you partway down the next page. Runs before paint so there's no
  // flash of mid-page content; instant so it never fights smooth scroll.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="header-scrim sticky top-0 z-30" data-scrolled={scrolled}>
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-6 px-5 py-3.5">
          {/* Brand — wordmark, a clean vertical hairline, then the tagline in
              spaced small-caps. The old horizontal "dash" ran into the words. */}
          <Link
            to={authed ? '/dashboard' : '/'}
            className="group flex items-center gap-3.5"
            aria-label="Gilding home"
          >
            <GoldWordmark className="h-10 sm:h-12" />
            <span
              className={cn(
                'items-center gap-3.5',
                // The tagline yields to the nav + instruments on tighter widths.
                authed ? 'hidden lg:flex' : 'hidden sm:flex',
              )}
            >
              <span aria-hidden="true" className="h-6 w-px bg-hairline-strong" />
              <span className="whitespace-nowrap font-display text-[10.5px] font-medium uppercase tracking-[0.2em] text-ink-subtle">
                A prompt amplifier
              </span>
            </span>
          </Link>

          {authed && !onLanding && (
            <nav className="hidden items-center gap-8 md:flex" aria-label="primary">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === '/dashboard'}
                  className="group relative py-1 outline-none"
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'font-display text-[0.95rem] transition-[color,transform] duration-200 ease-out',
                          'motion-safe:group-hover:-translate-y-px',
                          isActive ? 'text-ink' : 'text-ink-soft group-hover:text-ink',
                        )}
                      >
                        {n.label}
                      </span>
                      {/* Gilded underline draws in from the left on hover, holds
                          for the active route. */}
                      <span
                        aria-hidden="true"
                        className={cn(
                          'pointer-events-none absolute -bottom-0.5 left-0 h-px bg-accent transition-[width,opacity] duration-300 ease-out',
                          isActive
                            ? 'w-full opacity-100'
                            : 'w-0 opacity-0 group-hover:w-full group-hover:opacity-100',
                        )}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-2.5">
            {authed && meQuery.data?.credits !== undefined && (
              <Link
                to="/buy"
                aria-label="Get more leaves"
                title="Get more leaves"
                className="rounded-full transition-transform duration-200 hover:scale-[1.04] active:scale-95"
              >
                <CreditBadge credits={meQuery.data.credits} />
              </Link>
            )}
            {authed && !onLanding && (
              <span aria-hidden="true" className="mx-0.5 hidden h-5 w-px bg-hairline md:inline-block" />
            )}
            <ThemeToggle />
            {/* Returning visitors: a clean one-tap sign-in that skips the corridor. */}
            {!authed && <LoginButton />}
            {authed && <SignOutButton />}
          </div>
        </div>
        {/* Frontispiece base-rule — a quiet gild gradient under the header */}
        <div aria-hidden="true" className="gild-rule opacity-60" />
      </header>

      <main
        className={cn(
          'mx-auto w-full max-w-5xl flex-1 px-5',
          // The landing hero + the /buy forest scene are full-bleed, flush-to-the-
          // edges experiences — no frame padding, no separate footer gap.
          fullBleed ? 'py-0' : 'py-10 md:py-14',
        )}
      >
        {/* Keyed by path so each route rises in instead of hard-cutting. */}
        <div key={location.pathname} className="page-enter">
          <Outlet />
        </div>
      </main>

      {/* The fixed mobile nav overlays the viewport bottom — the footer (the
          last thing in the document) carries the clearance so the colophon
          can scroll fully above it. The landing route omits this: its hero
          carries its own colophon flush to the artwork, with no blank gap. */}
      {!fullBleed && (
        <footer className={cn('px-5 pb-10 pt-12', showBottomNav && 'pb-32 md:pb-10')}>
          <div className="mx-auto max-w-5xl">
            <div className="colophon-rule mb-3" aria-hidden="true">
              <FleuronMark />
            </div>
            <p className="text-center font-display italic text-caption text-ink-subtle">
              Gilding. Bring a sketch, leave with gold.
            </p>
            <nav
              aria-label="legal"
              className="mt-3 flex items-center justify-center gap-4 font-display text-micro uppercase tracking-[0.18em] text-ink-subtle"
            >
              <Link to="/privacy" className="transition-colors hover:text-ink-soft">
                Privacy
              </Link>
              <span aria-hidden="true" className="h-3 w-px bg-hairline" />
              <Link to="/terms" className="transition-colors hover:text-ink-soft">
                Terms
              </Link>
            </nav>
          </div>
        </footer>
      )}

      {showBottomNav && (
        <nav
          className="fixed bottom-0 left-0 right-0 z-30 bg-surface md:hidden"
          aria-label="primary"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Top hairline as a gilded rule, not a flat border */}
          <div aria-hidden="true" className="gild-rule opacity-50" />
          <ul className="mx-auto flex max-w-5xl items-stretch justify-around">
            {NAV.map((n) => (
              <li key={n.to} className="flex-1">
                <NavLink
                  to={n.to}
                  end={n.to === '/dashboard'}
                  className={({ isActive }) =>
                    cn(
                      'group flex min-h-[44px] flex-col items-center justify-center gap-1 py-2.5 font-display text-caption transition-colors duration-200',
                      isActive
                        ? 'font-semibold text-ink'
                        : 'font-medium text-ink-subtle hover:text-ink-soft',
                    )
                  }
                >
                  {/* No active dot — the icon + label already highlight in gold
                      ink when active, so the dot was redundant. */}
                  <>
                    <NavIcon name={n.icon} />
                    <span className="whitespace-nowrap leading-none">{n.label}</span>
                  </>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}

/**
 * LoginButton — the header's sign-in affordance, shown only when signed out. A
 * compact gilt pill (fine gold hairline, display-serif label, key glyph) so the
 * single most important signed-out action reads clearly and on-brand. One tap
 * runs the SIWE handshake and skips the corridor scroll (see useSiweLogin).
 */
function LoginButton() {
  const { busy, connect } = useSiweLogin();
  return (
    <button
      type="button"
      onClick={connect}
      disabled={busy}
      aria-label="Sign in"
      aria-busy={busy}
      className={cn(
        'group inline-flex h-9 items-center gap-1.5 rounded-full px-3.5',
        'border border-accent/40 bg-accent/5 font-display text-[0.9rem] text-ink',
        'transition-[background-color,border-color,box-shadow,transform] duration-200 ease-out',
        'hover:border-accent/70 hover:bg-accent/10 hover:shadow-[0_0_0_3px_rgba(233,200,115,0.08)]',
        'active:scale-[0.97] disabled:opacity-60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/45',
      )}
    >
      <KeyGlyph className="h-[15px] w-[15px] text-accent transition-colors duration-200 group-hover:text-accent-deep" />
      <span className="leading-none">{busy ? 'One moment…' : 'Sign in'}</span>
    </button>
  );
}

/** A small key — the sign-in / unlock-the-gallery mark for the header pill. */
function KeyGlyph({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="7.5" cy="7.5" r="3.3" />
      <path d="M9.9 9.9 16 16" />
      <path d="M13.4 13.4 14.9 11.9" />
      <path d="M15.2 15.2 16.7 13.7" />
    </svg>
  );
}

/**
 * SignOutButton — ends the wallet session. Clears the server cookie, the
 * local bearer token, and every cached query, then returns to the landing
 * page. Even if the network call fails, the local sign-out still happens —
 * the user asked to leave, so they leave.
 */
function SignOutButton() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const onSignOut = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    await disconnectWalletConnect();
    try {
      await api.logout();
    } catch {
      session.clear();
    }
    queryClient.clear();
    // Reset the entry flag so the gilded splash greets them again on sign-out.
    try {
      window.sessionStorage.removeItem(ENTERED_KEY);
    } catch {
      /* private-mode storage may throw; ignore */
    }
    navigate('/', { replace: true });
    setBusy(false);
  };

  return (
    <button
      type="button"
      onClick={() => void onSignOut()}
      disabled={busy}
      aria-label="Sign out"
      title="Sign out"
      className="inline-flex h-9 w-9 items-center justify-center rounded-sm text-ink-soft transition-colors duration-200 hover:text-ink disabled:opacity-50"
    >
      {/* Exit glyph — an open frame with the arrow stepping out of it. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        className="h-5 w-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 4.5H7a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h5" />
        <path d="M10.5 10h6.5" />
        <path d="M14.5 7.5 17 10l-2.5 2.5" />
      </svg>
    </button>
  );
}

/**
 * useScrolled — true once the page has moved more than a few pixels.
 * Drives the header scrim. Passive listener; state only flips at the
 * threshold so it never re-renders per scroll frame.
 */
function useScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/* ---------- Frontispiece ornaments — tiny bespoke SVGs ---------- */

/** A four-petal fleuron mark for the colophon line in the footer. */
function FleuronMark({ className }: { className?: string }) {
  const CLS = className ?? 'h-3.5 w-6';
  return (
    <svg viewBox="0 0 28 16" aria-hidden="true" className={CLS} >
      <defs>
        <linearGradient id="ornRamp" x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#a87f2c" />
          <stop offset="0.42" stopColor="#e9c873" />
          <stop offset="0.52" stopColor="#fff3ce" />
          <stop offset="0.64" stopColor="#c2922f" />
          <stop offset="1" stopColor="#7d521a" />
        </linearGradient>
      </defs>
      {/* slender cut lozenge with a bright facet seam */}
      <path d="M2 8 L14 3.2 L26 8 L14 12.8 Z" fill="url(#ornRamp)" />
      <path d="M2 8 L26 8" stroke="#fff3ce" strokeWidth="0.5" opacity="0.65" />
      <circle cx="14" cy="8" r="1.1" fill="#fff3ce" />
    </svg>
  );
}

/* ---------- Nav icons ---------- */

type IconName = 'new' | 'gallery' | 'feed' | 'leaderboard';

function NavIcon({ name }: { name: IconName }) {
  // The whole icon lifts and grows a touch on hover (desktop) and tap (mobile,
  // via group-active); each icon adds one bespoke flourish. All motion is
  // motion-safe so reduced-motion users get a still, crisp glyph.
  const svgClass = cn(
    'h-[22px] w-[22px] origin-bottom transition-transform duration-300 ease-out',
    'motion-safe:group-hover:-translate-y-[3px] motion-safe:group-active:-translate-y-[3px]',
    'motion-safe:group-hover:scale-[1.08] motion-safe:group-active:scale-[1.08]',
  );
  const part = 'transition-transform duration-300 ease-out';
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  switch (name) {
    case 'new':
      // Quill feather — compose. Tilts like lifting a pen as you reach for it.
      return (
        <svg
          {...common}
          className={cn(
            svgClass,
            'motion-safe:group-hover:-rotate-[12deg] motion-safe:group-active:-rotate-[12deg]',
          )}
        >
          <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z" />
          <path d="M16 8 2 22" />
          <path d="M17.5 15H9" />
        </svg>
      );
    case 'gallery':
      // A framed picture with a second frame fanning out behind it on hover.
      return (
        <svg {...common} className={svgClass}>
          <path
            className={cn(
              part,
              'motion-safe:group-hover:-translate-x-[1.5px] motion-safe:group-hover:translate-y-[1.5px]',
              'motion-safe:group-active:-translate-x-[1.5px] motion-safe:group-active:translate-y-[1.5px]',
            )}
            d="M16 20.5H6A2.5 2.5 0 0 1 3.5 18V8"
          />
          <rect x="7.5" y="3.5" width="13" height="13" rx="2.5" />
          <circle cx="11" cy="8" r="1.4" />
          <path d="M7.8 14.5 11 11.5l2.5 2 3-3 3.7 3.5" />
        </svg>
      );
    case 'feed':
      // A stack of post cards — the public stream.
      return (
        <svg {...common} className={svgClass}>
          <rect x="6" y="3.5" width="14" height="9" rx="2" />
          <rect x="3.5" y="9" width="14" height="11" rx="2.2" />
          <rect x="6" y="12" width="4" height="4" rx="1" />
          <path d="M11.5 13H15M11.5 15.5H14.5" />
        </svg>
      );
    case 'leaderboard':
      // Podium — centre tallest — with a star that twinkles on hover. Matches
      // the empty-podium on the leaderboard page.
      return (
        <svg {...common} className={svgClass}>
          <rect
            className={cn(
              part,
              'motion-safe:group-hover:-translate-y-[2px] motion-safe:group-active:-translate-y-[2px]',
            )}
            x="9.5"
            y="7"
            width="5"
            height="13"
            rx="1"
          />
          <rect x="3.5" y="11" width="5" height="9" rx="1" />
          <rect x="15.5" y="13" width="5" height="7" rx="1" />
          <path d="M2.5 20.5H21.5" />
          <path
            className={cn(
              part,
              'motion-safe:group-hover:scale-125 motion-safe:group-active:scale-125',
            )}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
            d="M12 1.8l.85 1.8 1.95.3-1.4 1.35.33 1.95L12 6.3l-1.73.9.33-1.95-1.4-1.35 1.95-.3z"
            fill="currentColor"
            stroke="none"
          />
        </svg>
      );
  }
}

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
}

const NAV: NavEntry[] = [
  { to: '/new', label: 'New', icon: 'new' },
  { to: '/gallery', label: 'Gallery', icon: 'gallery' },
  { to: '/feed', label: 'Feed', icon: 'feed' },
  { to: '/leaderboard', label: 'Ranks', icon: 'leaderboard' },
];
